import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LAYOUT_CONSTANTS } from '../../utils/layout-constants';

const {
  MIN_TITLE_WIDTH,
  TITLE_EDIT_BUTTON_WIDTH,
  TITLE_CENTER_PADDING,
  COMPACT_TO_FULL_HYSTERESIS_PX,
  LABELED_LEFT_FALLBACK_PX,
  FULL_RIGHT_FALLBACK_PX,
  COMPACT_VIEWER_INFO_ICON_WIDTH_PX,
} = LAYOUT_CONSTANTS.PRIMARY_BAR;

export function getPrimaryBarEffectiveFullRightWidth(
  visibleRightWidth: number,
  isCompactLayout: boolean,
  showViewerReadOnlyBanner: boolean,
  measuredFullReadOnlyBannerWidth: number
): number {
  if (!showViewerReadOnlyBanner || measuredFullReadOnlyBannerWidth <= 0 || !isCompactLayout) {
    return visibleRightWidth;
  }

  return visibleRightWidth - COMPACT_VIEWER_INFO_ICON_WIDTH_PX + measuredFullReadOnlyBannerWidth;
}

export interface PrimaryBarCompactLayoutMeasureInput {
  enabled: boolean;
  showProjectTitle: boolean;
  showTitleEditButton: boolean;
  isCompactLayout: boolean;
  rowClientWidth: number;
  labeledLeftWidth: number;
  fullRightWidth: number;
  measuredTitleWidth: number;
  lastLabeledLeftWidth: number;
  lastFullRightWidth: number;
}

export interface PrimaryBarCompactLayoutMeasureResult {
  isCompactLayout: boolean;
  lastLabeledLeftWidth: number;
  lastFullRightWidth: number;
}

export function getPrimaryBarTitleChromeWidth(measuredTitleWidth: number, showTitleEditButton: boolean): number {
  const titleWidth = measuredTitleWidth > 0 ? measuredTitleWidth : MIN_TITLE_WIDTH;
  const editWidth = showTitleEditButton ? TITLE_EDIT_BUTTON_WIDTH : 0;
  return titleWidth + editWidth + TITLE_CENTER_PADDING;
}

export function getPrimaryBarLabeledLayoutWidth(
  labeledLeftWidth: number,
  fullRightWidth: number,
  measuredTitleWidth: number,
  showProjectTitle: boolean,
  showTitleEditButton: boolean
): number {
  const leftWidth = labeledLeftWidth || LABELED_LEFT_FALLBACK_PX;
  const rightWidth = fullRightWidth || FULL_RIGHT_FALLBACK_PX;
  const titleWidth = showProjectTitle ? getPrimaryBarTitleChromeWidth(measuredTitleWidth, showTitleEditButton) : 0;
  return leftWidth + rightWidth + titleWidth;
}

export function measurePrimaryBarCompactLayout(input: PrimaryBarCompactLayoutMeasureInput): PrimaryBarCompactLayoutMeasureResult {
  const {
    enabled,
    showProjectTitle,
    showTitleEditButton,
    isCompactLayout,
    rowClientWidth,
    labeledLeftWidth,
    fullRightWidth,
    measuredTitleWidth,
    lastLabeledLeftWidth,
    lastFullRightWidth,
  } = input;

  if (!enabled) {
    return {
      isCompactLayout: false,
      lastLabeledLeftWidth,
      lastFullRightWidth,
    };
  }

  if (isCompactLayout) {
    const rightWidthForExpansion = Math.max(lastFullRightWidth, fullRightWidth);
    const requiredWidth = getPrimaryBarLabeledLayoutWidth(
      lastLabeledLeftWidth,
      rightWidthForExpansion,
      measuredTitleWidth,
      showProjectTitle,
      showTitleEditButton
    );
    return {
      isCompactLayout: rowClientWidth < requiredWidth + COMPACT_TO_FULL_HYSTERESIS_PX,
      lastLabeledLeftWidth,
      lastFullRightWidth,
    };
  }

  const currentLabeledLeft = labeledLeftWidth || lastLabeledLeftWidth;
  const currentFullRight = fullRightWidth || lastFullRightWidth;
  const requiredForLabeled = getPrimaryBarLabeledLayoutWidth(
    currentLabeledLeft,
    currentFullRight,
    measuredTitleWidth,
    showProjectTitle,
    showTitleEditButton
  );

  if (requiredForLabeled > rowClientWidth) {
    return {
      isCompactLayout: true,
      lastLabeledLeftWidth: currentLabeledLeft,
      lastFullRightWidth: currentFullRight,
    };
  }

  return {
    isCompactLayout: false,
    lastLabeledLeftWidth: currentLabeledLeft,
    lastFullRightWidth: currentFullRight,
  };
}

interface UsePrimaryBarCompactLayoutOptions {
  /** When false, always use the expanded (labeled) layout. */
  enabled: boolean;
  showProjectTitle: boolean;
  showTitleEditButton: boolean;
  /** When true, reserve the full read-only banner width even in compact mode. */
  showViewerReadOnlyBanner: boolean;
  /** Changes to this value re-run layout measurement (e.g. project name, access level). */
  remeasureKey?: string;
  /** Busts cached labeled/full widths when right-side chrome changes (e.g. viewer mode). */
  rightLayoutSignature?: string;
}

/**
 * Collapses primary-bar nav labels to icon-only before squeezing the project title.
 * Uses measured toolbar widths rather than viewport breakpoints.
 */
export function usePrimaryBarCompactLayout({
  enabled,
  showProjectTitle,
  showTitleEditButton,
  showViewerReadOnlyBanner,
  remeasureKey,
  rightLayoutSignature,
}: UsePrimaryBarCompactLayoutOptions) {
  const rowRef = useRef<HTMLDivElement>(null);
  const leftSectionRef = useRef<HTMLDivElement>(null);
  const rightSectionRef = useRef<HTMLDivElement>(null);
  const titleMeasureRef = useRef<HTMLSpanElement>(null);
  const readOnlyBannerMeasureRef = useRef<HTMLSpanElement>(null);
  const isCompactLayoutRef = useRef(false);
  const lastLabeledLeftWidthRef = useRef<number>(LABELED_LEFT_FALLBACK_PX);
  const lastFullRightWidthRef = useRef<number>(FULL_RIGHT_FALLBACK_PX);
  const measureCompactLayoutRef = useRef<() => void>(() => {});
  const [isCompactLayout, setIsCompactLayout] = useState(false);

  const measureCompactLayout = useCallback(() => {
    const row = rowRef.current;
    const leftSection = leftSectionRef.current;
    const rightSection = rightSectionRef.current;
    if (!row || !leftSection || !rightSection) {
      return;
    }

    const visibleRightWidth = rightSection.scrollWidth;
    const fullRightWidth = getPrimaryBarEffectiveFullRightWidth(
      visibleRightWidth,
      isCompactLayoutRef.current,
      showViewerReadOnlyBanner,
      readOnlyBannerMeasureRef.current?.scrollWidth ?? 0
    );

    const result = measurePrimaryBarCompactLayout({
      enabled,
      showProjectTitle,
      showTitleEditButton,
      isCompactLayout: isCompactLayoutRef.current,
      rowClientWidth: row.clientWidth,
      labeledLeftWidth: leftSection.scrollWidth,
      fullRightWidth,
      measuredTitleWidth: titleMeasureRef.current?.scrollWidth ?? 0,
      lastLabeledLeftWidth: lastLabeledLeftWidthRef.current,
      lastFullRightWidth: lastFullRightWidthRef.current,
    });

    lastLabeledLeftWidthRef.current = result.lastLabeledLeftWidth;
    lastFullRightWidthRef.current = result.lastFullRightWidth;

    if (result.isCompactLayout === isCompactLayoutRef.current) {
      return;
    }

    isCompactLayoutRef.current = result.isCompactLayout;
    setIsCompactLayout(result.isCompactLayout);
  }, [enabled, showProjectTitle, showTitleEditButton, showViewerReadOnlyBanner]);

  measureCompactLayoutRef.current = measureCompactLayout;

  useLayoutEffect(() => {
    lastLabeledLeftWidthRef.current = LABELED_LEFT_FALLBACK_PX;
    lastFullRightWidthRef.current = FULL_RIGHT_FALLBACK_PX;
    measureCompactLayoutRef.current();
    const frameId = requestAnimationFrame(() => measureCompactLayoutRef.current());
    return () => cancelAnimationFrame(frameId);
  }, [rightLayoutSignature]);

  useLayoutEffect(() => {
    measureCompactLayoutRef.current();
    const frameId = requestAnimationFrame(() => measureCompactLayoutRef.current());
    return () => cancelAnimationFrame(frameId);
  }, [measureCompactLayout]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const row = rowRef.current;
    const leftSection = leftSectionRef.current;
    const rightSection = rightSectionRef.current;
    const titleMeasure = titleMeasureRef.current;
    const readOnlyBannerMeasure = readOnlyBannerMeasureRef.current;
    if (!row) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      measureCompactLayoutRef.current();
    });

    resizeObserver.observe(row);
    if (leftSection) {
      resizeObserver.observe(leftSection);
    }
    if (rightSection) {
      resizeObserver.observe(rightSection);
    }
    if (titleMeasure) {
      resizeObserver.observe(titleMeasure);
    }
    if (readOnlyBannerMeasure) {
      resizeObserver.observe(readOnlyBannerMeasure);
    }

    let frameId: number | null = null;
    const scheduleMeasure = () => {
      if (frameId != null) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(() => {
        frameId = null;
        measureCompactLayoutRef.current();
      });
    };

    window.addEventListener('resize', scheduleMeasure);
    window.visualViewport?.addEventListener('resize', scheduleMeasure);

    return () => {
      if (frameId != null) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
      window.visualViewport?.removeEventListener('resize', scheduleMeasure);
    };
  }, [enabled, remeasureKey, rightLayoutSignature]);

  return {
    rowRef,
    leftSectionRef,
    rightSectionRef,
    titleMeasureRef,
    readOnlyBannerMeasureRef,
    isCompactLayout,
  };
}
